<template>
    <input
        :id="inputId"
        ref="autocomplete"
        type="text"
        :placeholder="placeholder"
        :autofocus="autofocus"
        :value="value"
        @input="updateValue($event.target.value)"
        @blur="onBlur"
        @keyup.enter="submitKey($event.target.value)"
    />
</template>

<script>
    import GoogleMapsLoader from 'google-maps';

    export default {
        props: {
            inputId: {
                type: String,
                default: 'addressInput',
            },
            value: {
                type: String,
                default: '',
            },
            placeholder: {
                type: String,
                default: 'Enter your street address here',
            },
            options: {
                type: Object,
                default() {
                    return {
                        types: ['address'],
                        componentRestrictions: { country: 'US' },
                    };
                },
            },
            autofocus: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                placesService: null,
                autocompleteService: null,
                googleAutocomplete: null,
            };
        },
        mounted() {
            const addressBox = document.getElementById(this.inputId);

            // load google-maps wrapper
            GoogleMapsLoader.LIBRARIES = ['places'];
            GoogleMapsLoader.KEY = 'AIzaSyC9FMzT6eDKWWcFtPRCMdyVgRiKW0fPBn0';

            GoogleMapsLoader.load((google) => {
                this.googleAutocomplete = new google.maps.places.Autocomplete(addressBox, this.options);
                this.autocompleteService = new google.maps.places.AutocompleteService();
                this.placesService = new google.maps.places.PlacesService(document.createElement('span'));
                google.maps.event.addListener(this.googleAutocomplete, 'place_changed', () => this.GetComponents());
            });
        },
        methods: {
            // This method is fired without the param by the Google Autocomplete event 'place_changed'
            // The argument is used in case of copy/paste
            GetComponents(tempData) {
                let placeData;

                // tempData variable is a parameter when copy/paste occurs
                if (tempData !== undefined) {
                    placeData = tempData;
                } else {
                    placeData = this.googleAutocomplete.getPlace();
                }

                // Checks to see if there is a geometry object in the place data, this determines if it is a valid place
                if (!placeData.geometry) {
                    this.$emit('check-valid-address', false);
                    return;
                }

                // Define which address components we want and which form we want
                const addressComponents = {
                    street_number: 'short_name',
                    route: 'long_name',
                    locality: 'long_name',
                    administrative_area_level_1: 'short_name',
                    postal_code: 'short_name',
                };

                const output = {};

                if (placeData.address_components !== undefined) {
                    const comps = placeData.address_components;

                    // Iterate through all address components returned and only grab the ones we want
                    for (let i = 0; i < comps.length; i++) {
                        const compType = comps[i].types[0];

                        if (addressComponents[compType]) {
                            output[compType] = comps[i][addressComponents[compType]];
                        }
                    }

                    // If the five components we desired from above are not all there, we want to let the parent app/component know
                    if (Object.keys(output).length !== 5) {
                        this.$emit('check-valid-address', false);
                        return;
                    }

                    // If we've made it through all of the checks above, the address is valid
                    this.$emit('check-valid-address', true);
                    // Signal to the parent app/component that the final set of address data is ready to be consumed.
                    this.$emit('placechanged', output, placeData, this.inputId);
                } else {
                    // If we've made it here, then there were no address components in the returned place data
                    this.$emit('check-valid-address', false);
                }
            },
            submitKey(value) {
                setTimeout(() => {
                    this.$emit('enter', value); // Give a chance to GetComponents to get executed
                }, 100);
            },
            updateValue(value) {
                this.$emit('input', value);
            },
            // This method is called on the blur event from the input box. It serves an additional event call
            //  for the google API to call the prediction and place service. This is due to the lack of handling
            //  of copy/paste into the box by Google. Additionally, this method will format typed addresses into
            //  the correct format for Google to handle by getting the prediction for the entered input.
            onBlur() {
                this.$emit('blur');

                // If there is no input in the box, then no need to submit it.
                if (this.$refs.autocomplete.value === '') return;

                // Get the predictions of the address based on the input
                this.autocompleteService.getPlacePredictions({ input: this.$refs.autocomplete.value }, (predictions, status) => {
                    if (status === 'OK') {
                        // If there's only one result in the predictions object, then that's likely the correct address
                        if (predictions.length === 1 && (predictions[0].terms.length === 4 || predictions[0].terms.length === 5)) {
                            // Get the place details for the given predicition's place. This is the data that we will send to GetComponents
                            //  to emulate what would typically happen if the place_changed event for the autocomplete box was fired
                            //  in normal circumstances
                            this.placesService.getDetails({ placeId: predictions[0].place_id }, (place, placeServiceStatus) => {
                                if (placeServiceStatus === 'OK') {
                                    this.GetComponents(place);
                                }
                            });
                        }
                    }
                });
            },
        },
    };
</script>
